import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();
  
  const links = [
    { label: 'resources', href: '/#resources' },
    { label: 'events', href: '/#events' },
  ];
  
  const handleNavClick = (href) => {
    setIsOpen(false);
    if (href.startsWith('/#')) {
      const element = document.querySelector(href.replace('/', ''));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {/* Transparent floating header */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="px-3 py-1.5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between px-4 py-1.5 rounded-full backdrop-blur-md transition-colors duration-500"
              style={{
                background: 'rgba(0, 0, 0, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}
            >
              {/* Left - Logo/Brand */}
              <div className="flex items-center">
                <span className="text-white/90 font-medium text-sm tracking-wide">data dreaming</span>
              </div>
              
              {/* Right nav */}
              <nav className="hidden md:flex items-center gap-6" role="navigation" aria-label="Main navigation">
                {links.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    className="text-sm font-medium transition-colors duration-300 text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-1"
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 text-white focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isOpen}
              >
                {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </motion.header>
      
      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed inset-0 z-40 pt-24 px-6 md:hidden ${
              theme === 'sunrise'
                ? 'bg-gradient-to-b from-sky-300 via-violet-300 to-purple-200'
                : 'bg-gradient-to-b from-slate-800 via-blue-700 to-teal-600'
            }`}
          >
            <nav className="flex flex-col items-center gap-8">
              {links.map((link, index) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleNavClick(link.href)}
                  className="text-2xl text-white font-medium"
                >
                  {link.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
